import { useState, useEffect, useMemo } from 'react';
import { authHeaders } from '../components/listPageStyles';
import { unwrapLookupItems } from '../components/c360';
import { relationLabel } from '../components/relationConfigs';
import { useDistributorUser } from './useDistributorUser';

/**
 * Talep 823/826/827/828/829 — kart ekranlarındaki ortak "Distribütör" seçim alanı.
 *
 * Kural (827/828/829 kelimesi kelimesine aynı; 823 ve 826 türevleri):
 *   - Alan ZORUNLU DEĞİL. Boş bırakılırsa tanım TÜM distribütörler için geçerlidir.
 *     Seçilirse yalnız seçili distribütör için geçerlidir.
 *   - Seçenekler /api/vendors/Select ucundan gelir. Bu uç zaten kullanıcının veri
 *     yetkisiyle (UserDataScope ScopeType=1 / DataScopeType.Distributor) daraltılmıştır
 *     (GetAllVendorsQueryHandler → CustomerScopeFilter.ScopeIdsOf). Admin tümünü görür.
 *     Bu yüzden burada YENİDEN filtreleme yapılmaz.
 *   - Kullanıcı "distribütör tipi" (User.IsDistributor) olarak tanımlandıysa yetkili
 *     distribütörü otomatik seçili gelir. 827/828/829 ayrıca alanın DEĞİŞTİRİLEMEZ
 *     olmasını ister; 826 (Müşteri kartı) yalnız otomatik seçim ister → kilit kararı
 *     çağıran ekrana bırakılır (`isDistributorUser` + `lockedVendorId` ayrı döner).
 *
 * Kullanıcının distribütör tipi olup olmadığı `useDistributorUser` hook'undan gelir
 * (/api/document-defaults/distributor-user). Bu hook bir zamanlar aynı bayrağı istemciden
 * iki ayrı uçla (User.IsDistributor + /datascopes) kendi türetiyordu; kural sunucuda tek
 * yerde durduğu için o kopya kaldırıldı — iki türetmenin zamanla ayrışması, aynı kullanıcının
 * bir ekranda kilitli bir ekranda serbest görünmesi demekti.
 */

/** Boş seçeneğin etiketi — "seçim yapılmadı" değil, açık bir ANLAM taşımalı. */
export const ALL_VENDORS_OPTION_LABEL = 'Tüm distribütörler (genel tanım)';

/**
 * @param {object}  [opts]
 * @param {boolean} [opts.enabled=true] — false ise hiç istek atılmaz. Alanı OPT-IN sunan
 *   paylaşılan bileşenler (ör. NamedDefinitionDetailPage onlarca tanım ekranını besler)
 *   için gerekli: hook koşullu çağrılamaz, ama isteği koşullu yapabiliriz.
 */
export function useDistributorScope({ enabled = true } = {}) {
  const [vendors, setVendors] = useState([]);

  // Distribütör tipi kullanıcı + tek yetkili distribütörü. Sunucu şu iki koşulu BİRLİKTE arar:
  // User.IsDistributor VE tam 1 adet Distribütör yetkisi (0 veya >1 ise kilitlenmez — tek doğru
  // cevap yokken kilit kullanıcıyı yanlış distribütöre hapsederdi). Admin asla kilitlenmez.
  const { isDistributor, vendorId: lockedVendorId, loading: userLoading } = useDistributorUser();
  const isDistributorUser = isDistributor;
  // Oto-seçim, hem seçenekler hem kullanıcı bilgisi çözülmeden uygulanmamalı (yükleme yarışı).
  const scopeLoaded = !enabled || !userLoading;

  // Seçenek listesi — kullanıcının yetkili olduğu distribütörler (uç zaten daraltıyor).
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    fetch('/api/vendors/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((j) => { if (alive) setVendors(unwrapLookupItems(j)); })
      .catch(() => { if (alive) setVendors([]); });
    return () => { alive = false; };
  }, [enabled]);

  // Boş seçenek = genel tanım. Zorunlu olmadığı için her zaman ilk sırada durur.
  const vendorOptions = useMemo(() => ([
    { value: '', label: ALL_VENDORS_OPTION_LABEL },
    ...vendors.map((v) => ({ value: String(v.id ?? v.Id), label: relationLabel(v) })),
  ]), [vendors]);

  // Kilitli seçenek listede yoksa (yetki listesi gecikirse) yine de etiketi görünsün.
  const lockedOptions = useMemo(() => {
    if (!lockedVendorId) return vendorOptions;
    const hit = vendorOptions.find((o) => o.value === lockedVendorId);
    return hit ? [hit] : [{ value: lockedVendorId, label: lockedVendorId }];
  }, [lockedVendorId, vendorOptions]);

  return {
    vendors,
    vendorOptions,
    lockedOptions,
    isDistributorUser,
    lockedVendorId,
    scopeLoaded,
  };
}

export default useDistributorScope;
